import { useEffect, useState } from "react";
import type { DashboardData } from "../types";
import { api, ApiError } from "../api";
import { formatCurrency } from "../format";
import ErrorState from "./ErrorState";
import { BarChart, PieChart } from "./charts";
import {
  IconBuilding,
  IconWallet,
  IconReceipt,
  IconList,
  IconSmile,
  IconRepeat,
  IconChart,
  IconTruck,
  IconGauge,
} from "../icons";

const COLORS = ["#0c7a3f", "#15a85a", "#34d27e", "#0a5c30", "#5cd99a", "#1f7a4d", "#8be0b3", "#0e9488"];

export default function Dashboard({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .dashboard()
      .then((d) => alive && setData(d))
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err : new ApiError(String(err)));
      });
    return () => {
      alive = false;
    };
  }, [refreshKey, reload]);

  if (error)
    return (
      <ErrorState
        message={error.message}
        code={error.code}
        onRetry={() => setReload((n) => n + 1)}
      />
    );
  if (!data) return <div className="empty">Carregando indicadores...</div>;

  const c = data.cards;
  const cards = [
    { label: "Obras ativas", value: c.obras_ativas, Icon: IconBuilding, tone: "green" },
    { label: "Valor programado (mês)", value: formatCurrency(c.valor_programado_mes), Icon: IconWallet, tone: "emerald" },
    { label: "Valor gasto (mês)", value: formatCurrency(c.valor_gasto_mes), Icon: IconReceipt, tone: "teal" },
    { label: "Atividades executadas", value: c.atividades_executadas, Icon: IconList, tone: "lime" },
    { label: "Satisfação média do cliente", value: `${c.satisfacao_media} / 10`, Icon: IconSmile, tone: "green" },
    { label: "Índices de retrabalho (mês)", value: c.retrabalho_mes, Icon: IconRepeat, tone: "amber" },
    { label: "Forecast planejado (próx. mês)", value: formatCurrency(c.forecast_planejado_prox), Icon: IconChart, tone: "emerald" },
    { label: "Forecast gasto (próx. mês)", value: formatCurrency(c.forecast_gasto_prox), Icon: IconChart, tone: "teal" },
    { label: "Veículos em manutenção", value: c.veiculos_manutencao, Icon: IconTruck, tone: "amber" },
    { label: "Equipamentos em atenção", value: c.equipamentos_atencao, Icon: IconGauge, tone: "red" },
  ];

  return (
    <div className="dashboard">
      <div className="cards">
        {cards.map((card) => (
          <div className={`card tone-${card.tone}`} key={card.label}>
            <div className="card-icon">
              <card.Icon size={22} />
            </div>
            <div className="card-body">
              <span className="card-value">{card.value}</span>
              <span className="card-label">{card.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="charts">
        <ChartBox title="Obras por colaborador">
          <BarChart
            data={data.obrasPorColaborador}
            xKey="colaborador"
            series={[{ key: "obras", label: "Obras", color: "#0c7a3f" }]}
          />
        </ChartBox>

        <ChartBox title="Programado x Gasto por mês (12 meses)">
          <BarChart
            data={data.programadoVsGasto}
            xKey="mes"
            series={[
              { key: "programado", label: "Programado (R$)", color: "#0c7a3f" },
              { key: "gasto", label: "Gasto (R$)", color: "#34d27e" },
            ]}
          />
        </ChartBox>

        <ChartBox title="Satisfação do cliente por nível">
          <PieChart
            data={data.satisfacaoPorNivel}
            valueKey="total"
            labelKey="nivel"
            colors={COLORS}
          />
        </ChartBox>

        <ChartBox title="Status das obras">
          <PieChart
            data={data.obrasPorStatus}
            valueKey="total"
            labelKey="status"
            colors={["#0c7a3f", "#15a85a", "#34d27e", "#c98a14", "#dc2626", "#94a3b8"]}
          />
        </ChartBox>

        <ChartBox title="Índices de retrabalho por colaborador">
          <BarChart
            data={data.retrabalhoPorColaborador}
            xKey="colaborador"
            series={[{ key: "quantidade", label: "Retrabalho", color: "#0e9488" }]}
          />
        </ChartBox>

        <ChartBox title="Forecast planejado x gasto por mês">
          <BarChart
            data={data.forecastPorMes}
            xKey="mes"
            series={[
              { key: "planejado", label: "Planejado (R$)", color: "#15a85a" },
              { key: "gasto", label: "Gasto (R$)", color: "#0e9488" },
            ]}
          />
        </ChartBox>
      </div>
    </div>
  );
}

function ChartBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="chart-box">
      <h3>{title}</h3>
      {children}
    </div>
  );
}
